import { useTranslation } from 'react-i18next'

/**
 * Areas of law of the "Law firm" template, validated by Michel on 24 Oct 2026.
 * The agent says these words to callers, in its own language: the template
 * receives them translated when it is applied.
 */
export const LAW_DOMAINS = [
  { id: 'family', fr: 'famille', en: 'family' },
  { id: 'immigration', fr: 'immigration', en: 'immigration' },
  { id: 'employment', fr: 'travail', en: 'employment' },
  { id: 'criminal', fr: 'criminel', en: 'criminal' },
  { id: 'real_estate', fr: 'immobilier', en: 'real estate' },
  { id: 'estates', fr: 'successions et testaments', en: 'wills and estates' },
  { id: 'business', fr: 'affaires', en: 'business' },
  { id: 'insurance', fr: 'assurances', en: 'insurance' },
  { id: 'civil_liability', fr: 'responsabilité civile', en: 'civil liability' },
  { id: 'tax', fr: 'fiscal', en: 'tax' },
] as const

export const DEFAULT_LAW_DOMAIN_IDS = ['family', 'immigration', 'employment', 'criminal']
/** Same limit as the server (telephony/services/modele_cabinet.py). */
export const MAX_LAW_DOMAINS = 12

export type LawLanguage = 'fr' | 'en'

export interface LawDomains {
  /** Checked areas, by id. */
  ids: string[]
  /** Areas not in the list, typed by the firm, separated by commas. */
  other: string
}

export const defaultLawDomains = (): LawDomains => ({ ids: [...DEFAULT_LAW_DOMAIN_IDS], other: '' })

/** The words the agent will use, in its language: checked areas first, then the typed ones. */
export function lawDomainLabels(value: LawDomains, lang: LawLanguage): string[] {
  const labels = LAW_DOMAINS.filter((d) => value.ids.includes(d.id)).map((d) => d[lang])
  const typed = value.other.split(',').map((s) => s.trim()).filter(Boolean)
  const seen = new Set<string>()
  return [...labels, ...typed].filter((l) => {
    const key = l.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Back from the words saved on the agent: known areas are checked, the rest goes to "Other area". */
export function lawDomainsFromLabels(labels: string[]): LawDomains {
  const ids: string[] = []
  const other: string[] = []
  for (const label of labels) {
    const key = label.trim().toLowerCase()
    const known = LAW_DOMAINS.find((d) => d.fr === key || d.en === key)
    if (known) {
      if (!ids.includes(known.id)) ids.push(known.id)
    } else if (key) {
      other.push(label.trim())
    }
  }
  return { ids, other: other.join(', ') }
}

interface Props {
  value: LawDomains
  onChange: (value: LawDomains) => void
  /** Language of the words shown: the agent's when it exists, otherwise the interface's. */
  lang: LawLanguage
  idPrefix: string
}

/** The areas of law of the firm: boxes to check, and one field for an area not in the list. */
export function LawDomainsPicker({ value, onChange, lang, idPrefix }: Props) {
  const { t } = useTranslation()
  const count = lawDomainLabels(value, lang).length
  const full = count >= MAX_LAW_DOMAINS

  const toggle = (id: string, checked: boolean) =>
    onChange({ ...value, ids: checked ? [...value.ids, id] : value.ids.filter((x) => x !== id) })

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
        {t('manager.aiAgents.lawFirm.domainsLabel')}
      </legend>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
        {LAW_DOMAINS.map((d) => {
          const checked = value.ids.includes(d.id)
          return (
            <label key={d.id} className="flex items-center gap-2.5 text-sm text-slate-800 dark:text-slate-200 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                disabled={!checked && full}
                onChange={(e) => toggle(d.id, e.target.checked)}
                className="h-4 w-4 rounded accent-blue-600 disabled:opacity-40"
              />
              <span className="inline-block first-letter:uppercase">{d[lang]}</span>
            </label>
          )
        })}
      </div>
      <div>
        <label htmlFor={`${idPrefix}-other`} className="block text-sm text-slate-700 dark:text-slate-300 mb-1">
          {t('manager.aiAgents.models.otherDomain')}
        </label>
        <input
          id={`${idPrefix}-other`}
          type="text"
          value={value.other}
          onChange={(e) => onChange({ ...value, other: e.target.value })}
          placeholder={t('manager.aiAgents.models.otherDomainPlaceholder')}
          className="w-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-base sm:text-sm text-slate-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
      </div>
      {/* Said only when something is wrong: nothing checked, or too many */}
      {count === 0 || count > MAX_LAW_DOMAINS ? (
        <p className="text-xs text-red-600 dark:text-red-400">
          {count === 0 ? t('manager.aiAgents.models.pickOne') : t('manager.aiAgents.models.tooMany', { max: MAX_LAW_DOMAINS })}
        </p>
      ) : null}
    </fieldset>
  )
}

/** Ready to apply: at least one area, no more than the server keeps. */
export const lawDomainsValid = (value: LawDomains, lang: LawLanguage) => {
  const n = lawDomainLabels(value, lang).length
  return n > 0 && n <= MAX_LAW_DOMAINS
}
